import React from 'react';
import { Star, Quote, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { useGoogleReviews } from '../hooks/useGoogleReviews';

const Testimonials = () => {
  const { rating, totalReviews, reviews, loading } = useGoogleReviews();

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  return (
    <section id="avaliacoes" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-[#1e3a8a]/10 rounded-full">
            <p className="text-[#1e3a8a] text-sm font-semibold">AVALIAÇÕES DO GOOGLE</p>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            O Que Nossos Clientes Estão Dizendo
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Mais de {totalReviews.toLocaleString()} clientes satisfeitos em Fortaleza confiam no nosso trabalho
          </p>

          {/* Overall Rating */}
          <div className="flex items-center justify-center gap-8 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star} 
                    className={`w-6 h-6 ${
                      star <= Math.floor(rating) 
                        ? 'fill-[#f59e0b] text-[#f59e0b]' 
                        : star === Math.ceil(rating) 
                          ? 'fill-[#f59e0b]/50 text-[#f59e0b]'
                          : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-3xl font-bold text-gray-900">{rating}</span>
              <span className="text-gray-600">de 5</span>
            </div>
            <div className="h-12 w-px bg-gray-300"></div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#1e3a8a]">{totalReviews}</p>
              <p className="text-gray-600">avaliações</p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-[#1e3a8a]"></div>
            <p className="mt-4 text-gray-600">Carregando avaliações...</p>
          </div>
        )}

        {/* Testimonials Grid */}
        {!loading && reviews.length > 0 && (
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
            {reviews.slice(0, 6).map((testimonial, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  {/* Quote Icon */}
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1e3a8a]/10 to-[#3b82f6]/10 rounded-full flex items-center justify-center mb-4">
                    <Quote className="w-6 h-6 text-[#1e3a8a]" />
                  </div>

                  {/* Stars */}
                  <div className="flex mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`w-4 h-4 ${
                          star <= testimonial.rating 
                            ? 'fill-[#f59e0b] text-[#f59e0b]' 
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-gray-700 mb-6 leading-relaxed line-clamp-4">
                    "{testimonial.text}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center">
                    {testimonial.profile_photo_url ? (
                      <img 
                        src={testimonial.profile_photo_url}
                        alt={testimonial.author_name}
                        className="w-10 h-10 rounded-full mr-3"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-full flex items-center justify-center mr-3">
                        <span className="text-white font-semibold text-sm">
                          {testimonial.author_name?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.author_name}</p>
                      <p className="text-sm text-gray-500">
                        {formatDate(testimonial.time)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* CTA to Google Reviews */}
        <div className="text-center">
          <a
            href="https://www.google.com/search?q=Gráfica+Rápida+Infortech+Fortaleza"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-[#1e3a8a] text-[#1e3a8a] rounded-xl font-semibold hover:bg-[#1e3a8a] hover:text-white transition-all hover:shadow-xl text-lg"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748z"/>
            </svg>
            Ver Todas as Avaliações no Google
            <ExternalLink className="w-5 h-5" />
          </a>
          <p className="mt-4 text-sm text-gray-500">
            Avaliações autênticas de clientes reais no Google Business Profile
          </p>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
